import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown } from "lucide-react";
import PageHeader from "@/components/PageHeader";
import { Stagger, staggerItem } from "@/components/Reveal";
import CTASection from "@/components/CTASection";
import { useSeo } from "@/lib/seo";
import { useBrand } from "@/context/SiteContext";
import { RESOURCE_TOPICS } from "@/data/content";

const FAQS = [
  { q: "What is a hybrid solar inverter?", a: "A hybrid solar inverter combines solar generation, battery storage and grid power into one integrated system — prioritising solar, charging batteries when required and providing backup during grid outages." },
  { q: "What is the difference between on-grid and off-grid systems?", a: "On-grid systems are connected to the electricity grid and are ideal for reducing bills. Off-grid systems use batteries to provide independent power where reliable grid electricity is unavailable." },
  { q: "Why choose LFP (LiFePO4) batteries?", a: "LFP batteries offer long cycle life, high usable capacity, fast charging and low maintenance, making them well suited to repeated charging and discharging in solar and backup applications." },
  { q: "Do you supply to dealers and EPC companies?", a: "Yes. We provide multi-brand product and complete BOS supply to solar dealers, installers, system integrators and EPC companies." },
  { q: "What does Solar BOS include?", a: "Balance of System covers mounting structures, DC-side and AC-side components, protection devices and accessories needed to install, connect, protect and operate a complete solar plant." },
];

export default function Resources() {
  const [open, setOpen] = useState(null);
  const brand = useBrand();
  useSeo({ title: `Resources & Guides | ${brand.name}`, description: "Solar, inverter, hybrid, battery, ESS, UPS, EV charging and BOS guides plus FAQs to help you make informed energy decisions.", canonical: `https://${brand.domain}/resources` });
  return (
    <div>
      <PageHeader label="Resources & Knowledge" title="Learn Before You Invest" sub="Practical guides and answers across solar, storage and power backup — written to help you make informed decisions." />
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <Stagger className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {RESOURCE_TOPICS.map((r) => (
            <motion.div key={r.title} variants={staggerItem} className="card-surface p-6">
              <div className="font-display font-semibold text-white text-lg">{r.title}</div>
              <p className="text-sm text-slate-400 mt-2">{r.text}</p>
              <span className="text-xs text-slate-500 mt-3 inline-block">Articles coming soon</span>
            </motion.div>
          ))}
        </Stagger>

        <div className="mt-16 max-w-3xl">
          <h2 className="font-display text-3xl font-bold text-white mb-6">Frequently Asked Questions</h2>
          <div className="space-y-3">
            {FAQS.map((f, i) => (
              <div key={i} className="card-surface overflow-hidden">
                <button onClick={() => setOpen(open === i ? null : i)} data-testid={`faq-toggle-${i}`} className="w-full flex items-center justify-between p-5 text-left">
                  <span className="font-medium text-white">{f.q}</span>
                  <ChevronDown className={`text-emerald-400 transition-transform ${open === i ? "rotate-180" : ""}`} size={20} />
                </button>
                <AnimatePresence>
                  {open === i && (
                    <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="px-5 pb-5 text-slate-400 text-sm leading-relaxed">
                      {f.a}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ))}
          </div>
        </div>
      </section>
      <CTASection />
    </div>
  );
}
